import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useSaveProgramStatusMutation,
  useUpdateProgramStatusMutation,
} from "../../../../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTypography,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../../../components/statusBadge/StatusBadge";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  formatDate,
  getFullName,
  getParsedErrorCode,
  getValueOrBlank,
  isNotEmpty,
  trimValue,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ProgramClosureFormKey,
  ProgramStatusFormKey,
  ProgramStatusList,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

import { InformationBadge } from "../../../../../../../../common/informationBadge/informationBadge";
import {
  BE_ERROR_CODES,
  EMPTY_VALUE_DASH,
  PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID,
  PROGRAM_CLOSURE_PROGRAM_WITHDRAWN_SITECORE_KEY,
} from "../../../../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { programStatusSelector } from "../../../../../../../../redux/slices/program/programSelector";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  ChangeProgramStatusClosed,
  ChangeProgramStatusProgramSchema,
} from "../../../../../../modelsAndSchemas/ValidationSchema";
import { ChangeProgramStatusWrapper } from "./ChangeProgramStatus.style";
import { ChangStatusFields } from "./components/changeStatusFields/ChangeStatusFields";
import { ProgramClosure } from "./components/programClosure/ProgramClosure";

interface ChangeProgramStatusProps {
  pageId: string;
  open: boolean;
  setShowChangeStatusModal: (value: boolean) => void;
  programId: string;
  isEditInactivateProgram?: boolean;
  isEditProgram?: boolean;
  inActiveStatusData?: ProgramStatusList;
  refetchProgramStatusList: () => void;
  onRefreshProgramDetail?: (id: string) => void;
  selectedDeliveryCountryId?: string;
  programStartDate?: string;
  closingReasonTypeKey?: string;
  showEditForReopen?: boolean;
  isUnbilledExpenses?: boolean;
}

export const ChangeProgramStatus: FC<ChangeProgramStatusProps> = ({
  pageId,
  open,
  setShowChangeStatusModal,
  programId,
  isEditInactivateProgram = false,
  isEditProgram,
  inActiveStatusData,
  refetchProgramStatusList,
  onRefreshProgramDetail,
  selectedDeliveryCountryId = "",
  programStartDate = "",
  closingReasonTypeKey = "",
  showEditForReopen = false,
  isUnbilledExpenses = false,
}) => {
  const oldProgramStatus = useSelector(programStatusSelector);
  const dispatch = useDispatch();
  const [statusList, setStatusList] = useState<OptionsDataType[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const { displayToast } = useToast();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showUnBilledExpenseWarningModal, setShowUnBilledExpenseWarningModal] =
    useState(false);
  const [secondaryClosingList, setSecondaryClosingList] = useState<
    OptionsDataType[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [selectedTab, setSelectedTab] = useState(0);

  // Mapping of statuses to their allowed dropdown values
  const statusOptions: Record<string, string[]> = {
    [STATUS_BADGE_TYPE.open]: [STATUS_BADGE_TYPE.closed],
    [STATUS_BADGE_TYPE.closed]: [STATUS_BADGE_TYPE.reOpen],
    [STATUS_BADGE_TYPE.active]: [
      STATUS_BADGE_TYPE.inActive,
      STATUS_BADGE_TYPE.closed,
    ],
    [STATUS_BADGE_TYPE.inActive]: [STATUS_BADGE_TYPE.active],
    [STATUS_BADGE_TYPE.conCon]: [
      STATUS_BADGE_TYPE.active,
      STATUS_BADGE_TYPE.closed,
    ],
  };

  const {
    mutateAsync: saveProgramStatusApi,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
    error: saveErrorData,
  } = useSaveProgramStatusMutation();

  //this api for update existing program status
  const {
    mutateAsync: updateProgramStatusApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
    error: updateErrorData,
  } = useUpdateProgramStatusMutation();

  useEffect(() => {
    if (isSaveError || isUpdateError) {
      const errorObj = isSaveError ? saveErrorData : updateErrorData;
      const errorCode = getParsedErrorCode(errorObj);

      switch (errorCode) {
        case BE_ERROR_CODES.PROGRAM_HAS_UNBILLED_FEES_EXPENSES:
          displayToast(
            getPopupLabel(programSiteCoreKeys.programClosureToastMessage),
          );
          break;
        case BE_ERROR_CODES.PROGRAM_REOPEN_PROJECT_WARNING:
          setShowWarningModal(true);
          break;
        case BE_ERROR_CODES.INACTIVE_PERIOD_OVERLAPPING:
          setErrorMessage(
            getPopupLabel(
              programSiteCoreKeys.overlappingPeriodsSavingErrorMessage,
            ),
          );
          break;

        default:
          displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
          break;
      }
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSaveError, isUpdateError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSavePending || isUpdatePending,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSavePending, isUpdatePending]);

  useEffect(() => {
    if (isSaveSuccess || isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: getPopupLabel(
            isSaveSuccess || showEditForReopen
              ? programSiteCoreKeys.programStatusUpdatedMessage
              : programSiteCoreKeys.inactivityUpdatedMessage,
          ),
        }),
      );

      if (isSaveSuccess || (showEditForReopen && isUpdateSuccess)) {
        onRefreshProgramDetail?.(programId);
      }
      refetchProgramStatusList();
      setShowChangeStatusModal(false);
    }
  }, [isSaveSuccess, isUpdateSuccess]);

  useEffect(() => {
    const statusDropdownValues = fetchDropdownValues(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.programStatusPopupStatusList,
    );

    const getDropdownOptions = (currentStatus: string) => {
      return statusDropdownValues.filter((option) =>
        statusOptions[currentStatus]?.includes(option.value as string),
      );
    };

    const finalStatusList = getDropdownOptions(oldProgramStatus);
    setStatusList([...finalStatusList]);
    setValue(
      ProgramStatusFormKey.programStatus,
      finalStatusList[0]?.value?.toString() ?? "",
    );
  }, [oldProgramStatus]);

  const defaultValues = {
    [ProgramStatusFormKey.programStatus]: oldProgramStatus,
    [ProgramStatusFormKey.fromDate]: "",
    [ProgramStatusFormKey.throughDate]: "",
    [ProgramStatusFormKey.notes]: "",
    [ProgramStatusFormKey.projectedProgramEndDate]: "",
  };

  const defaultLandedValues = {
    [ProgramClosureFormKey.landedDate]: "",
    [ProgramClosureFormKey.timeToLand]: EMPTY_VALUE_DASH,
    [ProgramClosureFormKey.totalHours]: EMPTY_VALUE_DASH,
    [ProgramClosureFormKey.totalNumberOfMeetings]: EMPTY_VALUE_DASH,
    [ProgramClosureFormKey.geographicalMove]: "",
    [ProgramClosureFormKey.landingCountry]: selectedDeliveryCountryId,
    [ProgramClosureFormKey.state]: "",
    [ProgramClosureFormKey.notes]: "",
    [ProgramClosureFormKey.newEmployeeName]: "",
    [ProgramClosureFormKey.newTitle]: "",
    [ProgramClosureFormKey.functionalChange]: "",
    [ProgramClosureFormKey.contractType]: "",
    [ProgramClosureFormKey.newOccupationalFunction]: "",
    [ProgramClosureFormKey.newOccupationalLevel]: "",
    [ProgramClosureFormKey.newOccupationalFunctionOther]: "",
    [ProgramClosureFormKey.industryChange]: "",
    [ProgramClosureFormKey.industryList]: "",
    [ProgramClosureFormKey.newJobStartDate]: "",
    [ProgramClosureFormKey.compensationComparison]: "",
    [ProgramClosureFormKey.placementMethod]: "",
    [ProgramClosureFormKey.postalCode]: "",
    [ProgramClosureFormKey.workLocationType]: "",
    [ProgramClosureFormKey.dateOfEndOfTrialPeriod]: "",
    [ProgramClosureFormKey.currentSalaryRange]: "",
    [ProgramClosureFormKey.newSalaryRange]: "",
    [ProgramClosureFormKey.newEmployerVATNumber]: "",
    [ProgramClosureFormKey.newLevel]: "",
    [ProgramClosureFormKey.solutionValidationDate]: "",
    [ProgramClosureFormKey.newEmploymentContractType]: "",
    [ProgramClosureFormKey.selectedPrimaryClosingReasonSitecoreKey]: "",
  };

  const defaultClosureValues = {
    [ProgramClosureFormKey.primaryClosingReason]: "",
    [ProgramClosureFormKey.secondaryClosingReason]: "",
    [ProgramClosureFormKey.closedDate]: dayjs().format(userCountryDateFormat),
    [ProgramClosureFormKey.closedBy]: getFullName(
      getValueOrBlank(loggedInUser?.firstName),
      getValueOrBlank(loggedInUser?.lastName),
      true,
    ),
    ...defaultLandedValues,
  };

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.programStatusKey, key);
  };

  const getPopupLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      ChangeProgramStatusProgramSchema(
        {
          ...commonValidationMessages,
          throughDateValidationMessage: getPopupLabel(
            programSiteCoreKeys.throughDateValidationMessage,
          ),
          notesRequiredValidationMessage: getPopupLabel(
            programSiteCoreKeys.notesIsRequired,
          ),
        },
        isEditInactivateProgram,
        userCountryDateFormat ?? "",
        showEditForReopen,
      ),
    ),
  });

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { isDirty },
    setValue,
  } = methods;

  //closure methods
  const closureMethods = useForm({
    mode: "all",
    defaultValues: defaultClosureValues,
    resolver: yupResolver(
      ChangeProgramStatusClosed(
        {
          ...commonValidationMessages,
          solutionValidationDateErrorMessage: fetchLabel(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.solutionValidationDateErrorMessage,
          ),
        },
        secondaryClosingList,
        selectedDeliveryCountryId,
        userCountryDateFormat,
      ),
    ),
  });

  // for reset the value if edit inactivate program status click
  useEffect(() => {
    if (
      (isEditInactivateProgram || showEditForReopen) &&
      isNotEmpty(inActiveStatusData)
    ) {
      const values = {
        [ProgramStatusFormKey.programStatus]: inActiveStatusData?.statusKey,
        [ProgramStatusFormKey.fromDate]: formatDate(
          inActiveStatusData?.fromDate ?? "",
          userCountryDateFormat,
        ),

        [ProgramStatusFormKey.notes]: inActiveStatusData?.notes ?? "",

        ...(isEditInactivateProgram && {
          [ProgramStatusFormKey.throughDate]: formatDate(
            inActiveStatusData?.throughDate ?? "",
            userCountryDateFormat,
          ),
        }),

        ...(showEditForReopen && {
          [ProgramStatusFormKey.projectedProgramEndDate]: formatDate(
            inActiveStatusData?.projectedProgramEndDate ?? "",
            userCountryDateFormat,
          ),
        }),
      };
      reset(values);
    }
  }, [isEditInactivateProgram, inActiveStatusData, showEditForReopen]);

  const handleSaveProgramDetails = () => {
    const {
      programStatus,
      fromDate,
      throughDate,
      notes,
      projectedProgramEndDate,
    } = getValues();

    const isProgramStatusReOpen = programStatus === STATUS_BADGE_TYPE.reOpen;
    const params = {
      programId: programId,
      status: programStatus,
      fromDate: fromDate
        ? convertToUTCFormat(fromDate, userCountryDateFormat)
        : "",
      throughDate: throughDate
        ? convertToUTCFormat(throughDate, userCountryDateFormat)
        : "",
      notes: trimValue(notes),
      ...((isProgramStatusReOpen || showEditForReopen) &&
        isNotEmpty(projectedProgramEndDate) && {
          projectedProgramEndDate: convertToUTCFormat(
            projectedProgramEndDate,
            userCountryDateFormat,
          ),
        }),
    };
    if (isEditInactivateProgram) {
      updateProgramStatusApi({
        programId: params.programId,
        statusId: inActiveStatusData?.id ?? "",
        throughDate: params.throughDate,
        notes: params.notes,
      });
    } else if (showEditForReopen) {
      updateProgramStatusApi({
        programId: params.programId,
        statusId: inActiveStatusData?.id ?? "",
        fromDate: params.fromDate,
        projectedProgramEndDate: params.projectedProgramEndDate,
        notes: params.notes,
      });
    } else {
      saveProgramStatusApi(params);
    }
  };

  const forceCloseProgramDespiteUnbilledExpenses = () => {
    handleSubmitFormData(true);
    handleUnBilledWarningConfirmationModalClose();
  };

  const handleUnBilledWarningConfirmationModalClose = () => {
    setShowUnBilledExpenseWarningModal(false);
  };

  const handleWarningConfirmationModalClose = () => {
    setShowWarningModal(false);
  };

  const handleCancelOkClick = () => {
    setShowCancelModal(false);
    setShowChangeStatusModal(false);
  };

  const handleCancelChangeStatus = () => {
    if (isDirty || isDirtyClosure) {
      setShowCancelModal(true);
    } else {
      setShowChangeStatusModal(false);
    }
    handleErrorReset();
  };

  const {
    getValues: getClosureValues,
    reset: resetClosureFrom,
    watch: watchClosure,
    formState: { isDirty: isDirtyClosure },
    handleSubmit: handleSubmitClosure,
    trigger: triggerClosure,
  } = closureMethods;

  const isLandedInformationVisible =
    watchClosure(ProgramClosureFormKey.primaryClosingReason) ===
    PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID;

  const isSelectedProgramStatusClosed = useMemo(() => {
    const { programStatus: status } = getValues();
    if (status === STATUS_BADGE_TYPE.reOpen) {
      setValue(
        ProgramStatusFormKey.fromDate,
        dayjs().format(userCountryDateFormat),
      );
    }
    if (
      oldProgramStatus === STATUS_BADGE_TYPE.active ||
      oldProgramStatus === STATUS_BADGE_TYPE.conCon
    ) {
      if (status === STATUS_BADGE_TYPE.closed) {
        resetClosureFrom();
        return true;
      } else {
        reset();
        setValue(ProgramStatusFormKey.programStatus, status);
      }
      return false;
    }
    if (oldProgramStatus === STATUS_BADGE_TYPE.open) {
      if (status === STATUS_BADGE_TYPE.closed) {
        resetClosureFrom();
        return true;
      }
      return false;
    }
    return false;
  }, [watch(ProgramStatusFormKey.programStatus)]);

  const resetLandedValues = () => {
    const { primaryClosingReason, secondaryClosingReason } = getClosureValues();
    resetClosureFrom({
      ...defaultClosureValues,
      [ProgramClosureFormKey.primaryClosingReason]: primaryClosingReason,
      [ProgramClosureFormKey.secondaryClosingReason]: secondaryClosingReason,
    });
  };

  const prepareLandedValues = () => {
    const {
      totalNumberOfMeetings,
      landedDate,
      timeToLand,
      geographicalMove,
      landingCountry,
      notes,
      newTitle,
      functionalChange,
      newOccupationalFunction,
      newOccupationalLevel,
      newOccupationalFunctionOther,
      industryChange,
      industryList,
      newJobStartDate,
      compensationComparison,
      placementMethod,
      postalCode,
      newEmployeeName,
      workLocationType,
      dateOfEndOfTrialPeriod,
      currentSalaryRange,
      newSalaryRange,
      newEmployerVATNumber,
      newLevel,
      state,
      solutionValidationDate,
      newEmploymentContractType,
    } = getClosureValues();

    let dateObjects = {};

    if (isNotEmpty(solutionValidationDate)) {
      dateObjects = {
        solutionValidatedDate: convertToUTCFormat(
          solutionValidationDate,
          userCountryDateFormat ?? "",
        ),
      };
    }
    if (isNotEmpty(landedDate)) {
      dateObjects = {
        ...dateObjects,
        landedDate: convertToUTCFormat(landedDate, userCountryDateFormat ?? ""),
      };
    }
    if (isNotEmpty(newJobStartDate)) {
      dateObjects = {
        ...dateObjects,
        newJobStartDate: convertToUTCFormat(
          newJobStartDate,
          userCountryDateFormat ?? "",
        ),
      };
    }

    if (isNotEmpty(dateOfEndOfTrialPeriod)) {
      dateObjects = {
        ...dateObjects,
        endDateOfTrialPeriod: convertToUTCFormat(
          dateOfEndOfTrialPeriod,
          userCountryDateFormat ?? "",
        ),
      };
    }

    return {
      geogrphicMove: geographicalMove,
      totalMeetings:
        timeToLand !== EMPTY_VALUE_DASH ? Number(totalNumberOfMeetings) : 0,
      landingCountry: landingCountry,
      timeToLandInDays:
        timeToLand !== EMPTY_VALUE_DASH ? Number(timeToLand) : 0,
      newTitle,
      functionalChange: functionalChange,
      newOccupationalFunction: newOccupationalFunction,
      newOccupationalFunctionText: newOccupationalFunctionOther,
      occupationalLevel: newOccupationalLevel,
      industryChange,
      industryList,
      placementMethod,
      compansationComparision: compensationComparison,
      notes,
      postalCode,
      newEmployerName: newEmployeeName,
      workLocationType,
      currentSalaryRange,
      newSalaryRange,
      state,
      newEmployerVAT: newEmployerVATNumber,
      newLevel,
      newEmploymentContractType,
      ...dateObjects,
    };
  };

  const handleSaveProgramClosureDetails = () => {
    const { programStatus } = getValues();
    const { primaryClosingReason, secondaryClosingReason } = getClosureValues();

    let params = {
      programId: programId,
      status: programStatus,
      primaryClosingReason,
      secondaryClosingReason,
    };

    if (primaryClosingReason === PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID) {
      const landedValues = prepareLandedValues();
      params = {
        ...params,
        ...landedValues,
      };
    }
    saveProgramStatusApi(params);
  };

  const checkGeneralValidation = (tabIndex: number): Promise<boolean> => {
    return triggerClosure([
      ProgramClosureFormKey.secondaryClosingReason,
      ProgramClosureFormKey.geographicalMove,
      ProgramClosureFormKey.landingCountry,
      ProgramClosureFormKey.state,
      ProgramClosureFormKey.postalCode,
    ]).then((isValid) => {
      if (!isValid) return false;
      else {
        setSelectedTab(tabIndex);
        return true;
      }
    });
  };

  const handleSubmitFormData = (
    userAcceptedClosureForUnbilledExpenses: boolean = false,
  ) => {
    const { programStatus: newProgramStatus } = getValues();
    const { selectedPrimaryClosingReasonSitecoreKey } = getClosureValues();
    if (
      isUnbilledExpenses &&
      (oldProgramStatus === STATUS_BADGE_TYPE.open ||
        (oldProgramStatus === STATUS_BADGE_TYPE.active &&
          selectedPrimaryClosingReasonSitecoreKey ===
            PROGRAM_CLOSURE_PROGRAM_WITHDRAWN_SITECORE_KEY)) &&
      !userAcceptedClosureForUnbilledExpenses
    ) {
      setShowUnBilledExpenseWarningModal(true);
    }
    //if program status close then save the program closure details and in else it will be normal status change
    else if (newProgramStatus === STATUS_BADGE_TYPE.closed) {
      checkGeneralValidation(selectedTab).then((isValid) => {
        if (isValid) {
          if (isLandedInformationVisible && selectedTab < 1) {
            setSelectedTab(selectedTab + 1);
            return;
          }
          handleSubmitClosure(handleSaveProgramClosureDetails)();
        }
      });
    } else {
      handleSubmit(handleSaveProgramDetails)();
    }
  };

  const handleErrorReset = () => {
    setErrorMessage(undefined);
  };

  let primaryButtonTitle = commonLabels.save;
  if (isLandedInformationVisible && selectedTab < 1) {
    primaryButtonTitle = commonLabels.next;
  } else if (isEditInactivateProgram) {
    primaryButtonTitle = commonLabels.update;
  }

  return (
    <MuiModal
      onClose={() => setShowChangeStatusModal(false)}
      title={
        showEditForReopen
          ? getPopupLabel(programSiteCoreKeys.editStatusHeader)
          : isEditInactivateProgram
            ? getPopupLabel(programSiteCoreKeys.editInactivityPopupHeader)
            : getLabel(programSiteCoreKeys.changeStatusPopupHeader)
      }
      open={open}
      primaryButtonTitle={primaryButtonTitle}
      secondaryButtonTitle={commonLabels.cancel}
      hasLargeContent
      onSecondaryButtonClick={handleCancelChangeStatus}
      onPrimaryButtonClick={() => handleSubmitFormData(false)}
      footerErrorMessage={errorMessage}
    >
      <ChangeProgramStatusWrapper>
        {showEditForReopen && (
          <InformationBadge
            message={getPopupLabel(
              programSiteCoreKeys.programReOpenedNoteMessage,
            )}
          />
        )}
        <MuiGrid
          container
          className="form-container status-dropdown"
          data-testid="change-status-modal"
        >
          <MuiGrid size={{ md: 4, sm: 6, xs: 12 }} className="form-field">
            {isEditInactivateProgram || showEditForReopen ? (
              <MuiBox className="status-container">
                <MuiTypography variant="h3" className="status-label">
                  {getLabel(programSiteCoreKeys.changeProgramStatus)}
                </MuiTypography>
                <StatusBadge
                  status={
                    showEditForReopen
                      ? STATUS_BADGE_TYPE.closed
                      : inActiveStatusData?.statusKey
                  }
                  label={fetchValueByKeyOfDropdown(
                    programSiteCoreKeys.changeStatusModelPopupKey,
                    programSiteCoreKeys.programStatusPopupStatusList,
                    showEditForReopen
                      ? STATUS_BADGE_TYPE.closed
                      : getValueOrBlank(inActiveStatusData?.statusKey),
                  )}
                ></StatusBadge>
              </MuiBox>
            ) : (
              <FormController
                control={control}
                name={ProgramStatusFormKey.programStatus}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.changeProgramStatus)}
                  options={statusList}
                  onChange={handleErrorReset}
                  id={`${pageId}-${ProgramStatusFormKey.programStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  searchPlaceholder={commonLabels.search}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            )}
          </MuiGrid>
        </MuiGrid>
        {isSelectedProgramStatusClosed ? (
          <FormProvider {...closureMethods}>
            <ProgramClosure
              pageId={pageId}
              programId={programId}
              resetLandedValues={resetLandedValues}
              userCountryDateFormat={userCountryDateFormat}
              programStartDate={programStartDate}
              closingReasonTypeKey={closingReasonTypeKey}
              secondaryClosingList={secondaryClosingList}
              setSecondaryClosingList={setSecondaryClosingList}
              currentProgramStatus={oldProgramStatus}
              deliveryCountry={selectedDeliveryCountryId}
              isVisibleLandingInformation={isLandedInformationVisible}
              selectedTab={selectedTab}
              onTabChangeCallback={(tabIndex) =>
                checkGeneralValidation(tabIndex)
              }
            />
          </FormProvider>
        ) : (
          <FormProvider {...methods}>
            <ChangStatusFields
              pageId={pageId}
              isEditInactivateProgram={isEditInactivateProgram}
              userCountryDateFormat={userCountryDateFormat}
              showEditForReopen={showEditForReopen}
              handleErrorReset={handleErrorReset}
              isEditProgram={isEditProgram}
              programId={programId}
            />
          </FormProvider>
        )}
        {showCancelModal && (
          <CancelFieldModal
            showModal={showCancelModal}
            handleModalClose={() => setShowCancelModal(false)}
            handlePrimaryButtonClick={handleCancelOkClick}
          />
        )}
        {showWarningModal && (
          <WarningConfirmationModal
            show={showWarningModal}
            onClose={handleWarningConfirmationModalClose}
            onPrimaryButtonClick={handleWarningConfirmationModalClose}
            primaryButtonTitle={commonLabels.ok}
            showSecondaryButton={false}
            labelContent={getPopupLabel(
              programSiteCoreKeys.programReOpenPromptMessage,
            )}
          />
        )}

        {showUnBilledExpenseWarningModal && (
          <WarningConfirmationModal
            show={showUnBilledExpenseWarningModal}
            onClose={handleUnBilledWarningConfirmationModalClose}
            onPrimaryButtonClick={forceCloseProgramDespiteUnbilledExpenses}
            primaryButtonTitle={commonLabels.yesProceed}
            secondaryButtonTitle={commonLabels.no}
            onSecondaryButtonClick={handleUnBilledWarningConfirmationModalClose}
            labelContent={getPopupLabel(
              programSiteCoreKeys.programCloseConfirmationMessage,
            )}
          />
        )}
      </ChangeProgramStatusWrapper>
    </MuiModal>
  );
};
